import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useCallback, useState, useContext, useMemo } from 'react';
import { AnnotationTypes } from '@atlaskit/adf-schema';
import { fg } from '@atlaskit/platform-feature-flags';
import { updateWindowSelectionAroundDraft } from '../draft/dom';
import { FabricChannel } from '@atlaskit/analytics-listeners/types';
import { ACTION, ACTION_SUBJECT, EVENT_TYPE, ACTION_SUBJECT_ID } from '@atlaskit/editor-common/analytics';
import { getRendererRangeInlineNodeNames } from '../../../actions/get-renderer-range-inline-node-names';
import { RendererContext as ActionsContext } from '../../RendererActionsContext';
export var SelectionInlineCommentMounter = /*#__PURE__*/React.memo(function (props) {
  var Component = props.component,
    range = props.range,
    draftRange = props.draftRange,
    isAnnotationAllowed = props.isAnnotationAllowed,
    wrapperDOM = props.wrapperDOM,
    onCloseProps = props.onClose,
    clearAnnotationDraft = props.clearAnnotationDraft,
    applyAnnotationDraftAt = props.applyAnnotationDraftAt,
    documentPosition = props.documentPosition,
    applyAnnotation = props.applyAnnotation,
    createAnalyticsEvent = props.createAnalyticsEvent,
    generateIndexMatch = props.generateIndexMatch;
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    draftDocumentPosition = _useState2[0],
    setDraftDocumentPosition = _useState2[1];
  var actions = useContext(ActionsContext);
  var inlineNodeTypes = useMemo(function () {
    if (fg('annotations_defensive_node_name_calculations')) {
      if (!actions.isRangeAnnotatable(range)) {
        return undefined;
      }
      return getRendererRangeInlineNodeNames({
        pos: documentPosition,
        actions: actions
      });
    } else {
      if (!fg('editor_inline_comments_on_inline_nodes')) {
        return undefined;
      }
      if (actions.isValidAnnotationRange(range)) {
        return getRendererRangeInlineNodeNames({
          pos: documentPosition,
          actions: actions
        });
      } else {
        return undefined;
      }
    }
  }, [documentPosition, actions, range]);
  var onCreateCallback = useCallback(function (annotationId) {
    // We want to support creation on a documentPosition if the user is only using ranges
    // but we want to prioritize draft positions if they are being used by consumers
    var positionToAnnotate = draftDocumentPosition || documentPosition;
    if (!positionToAnnotate || !applyAnnotation) {
      // TODO: EDITOR-595 - This analytic event is temporary and should be removed once the following issue
      // has been identified and fixed: https://atlassian.slack.com/archives/C08JK0WSCH5/p1745902609966999
      if (createAnalyticsEvent && fg('platform_renderer_annotations_create_debug_logging')) {
        createAnalyticsEvent({
          action: 'failed',
          actionSubject: 'applyAnnotation',
          actionSubjectId: 'inlineCommentFailureReason',
          attributes: {
            reason: 'Annotation Position invalid',
            draftDocumentPosition: draftDocumentPosition,
            documentPosition: documentPosition,
            applyAnnotation: !!applyAnnotation
          },
          eventType: EVENT_TYPE.OPERATIONAL
        }).fire(FabricChannel.editor);
      }
      return false;
    }

    // Evaluate position validity when the user commits the position to be annotated
    var isCreateAllowedOnPosition = actions.isValidAnnotationPosition(positionToAnnotate);
    if (!isCreateAllowedOnPosition) {
      return false;
    }
    var annotation = {
      annotationId: annotationId,
      annotationType: AnnotationTypes.INLINE_COMMENT
    };
    if (createAnalyticsEvent) {
      createAnalyticsEvent({
        action: ACTION.INSERTED,
        actionSubject: ACTION_SUBJECT.ANNOTATION,
        actionSubjectId: ACTION_SUBJECT_ID.INLINE_COMMENT,
        attributes: {
          inlineNodeNames: inlineNodeTypes
        },
        eventType: EVENT_TYPE.TRACK
      }).fire(FabricChannel.editor);
    }
    return applyAnnotation(positionToAnnotate, annotation);
  }, [actions, documentPosition, applyAnnotation, draftDocumentPosition, createAnalyticsEvent, inlineNodeTypes]);
  var createIndexCallback = useCallback(function () {
    if (!documentPosition || !generateIndexMatch) {
      return false;
    }
    var result = generateIndexMatch(documentPosition);
    if (!result) {
      return false;
    }
    return result;
  }, [documentPosition, generateIndexMatch]);
  var applyDraftModeCallback = useCallback(function (options) {
    if (!documentPosition || !isAnnotationAllowed) {
      if (createAnalyticsEvent) {
        createAnalyticsEvent({
          action: ACTION.CREATE_NOT_ALLOWED,
          actionSubject: ACTION_SUBJECT.ANNOTATION,
          actionSubjectId: ACTION_SUBJECT_ID.INLINE_COMMENT,
          attributes: {
            inlineNodeNames: inlineNodeTypes
          },
          eventType: EVENT_TYPE.TRACK
        }).fire(FabricChannel.editor);
      }
      return false;
    }
    setDraftDocumentPosition(documentPosition);
    applyAnnotationDraftAt(documentPosition);
    if (createAnalyticsEvent) {
      var uniqueAnnotationsInRange = range ? actions.getAnnotationsByPosition(range) : [];
      createAnalyticsEvent({
        action: ACTION.OPENED,
        actionSubject: ACTION_SUBJECT.ANNOTATION,
        actionSubjectId: ACTION_SUBJECT_ID.INLINE_COMMENT,
        eventType: EVENT_TYPE.TRACK,
        attributes: {
          overlap: uniqueAnnotationsInRange.length,
          inlineNodeNames: inlineNodeTypes
        }
      }).fire(FabricChannel.editor);
    }
    window.requestAnimationFrame(function () {
      if (options.keepNativeSelection) {
        updateWindowSelectionAroundDraft(documentPosition);
      } else {
        var sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
        }
      }
    });
    var positionToAnnotate = draftDocumentPosition || documentPosition;
    if (!positionToAnnotate || !applyAnnotation || !options.annotationId) {
      return false;
    }
    var annotation = {
      annotationId: options.annotationId,
      annotationType: AnnotationTypes.INLINE_COMMENT
    };
    return applyAnnotation(positionToAnnotate, annotation);
  }, [documentPosition, isAnnotationAllowed, applyAnnotationDraftAt, createAnalyticsEvent, applyAnnotation, draftDocumentPosition, actions, range, inlineNodeTypes]);
  var removeDraftModeCallback = useCallback(function () {
    clearAnnotationDraft();
    setDraftDocumentPosition(null);
    var sel = window.getSelection();
    if (sel) {
      sel.removeAllRanges();
    }
  }, [clearAnnotationDraft]);
  var onCloseCallback = useCallback(function () {
    if (createAnalyticsEvent) {
      createAnalyticsEvent({
        action: ACTION.CLOSED,
        actionSubject: ACTION_SUBJECT.ANNOTATION,
        actionSubjectId: ACTION_SUBJECT_ID.INLINE_COMMENT,
        eventType: EVENT_TYPE.TRACK,
        attributes: {
          inlineNodeNames: inlineNodeTypes
        }
      }).fire(FabricChannel.editor);
    }
    removeDraftModeCallback();
    onCloseProps();
  }, [onCloseProps, removeDraftModeCallback, createAnalyticsEvent, inlineNodeTypes]);
  return /*#__PURE__*/React.createElement(Component, {
    range: range,
    draftRange: draftRange
    // Ignored via go/ees005
    // eslint-disable-next-line @atlaskit/editor/no-as-casting
    ,
    wrapperDOM: wrapperDOM.current,
    isAnnotationAllowed: isAnnotationAllowed,
    onClose: onCloseCallback,
    onCreate: onCreateCallback,
    getAnnotationIndexMatch: createIndexCallback,
    applyDraftMode: applyDraftModeCallback,
    removeDraftMode: removeDraftModeCallback,
    inlineNodeTypes: inlineNodeTypes
  });
});